import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type {
  AssistantFunction,
  AssistantInfo,
  AssistantValue,
} from "../../../services/api/instances/assistantsApi/types";
import {
  changeResponseFormatReducerBuilder,
  deleteAssistantFunctionsReducerBuilder,
  getAssistantFunctionsReducerBuilder,
  getAssistantMessagesReducerBuilder,
  getAssistantSkillsReducerBuilder,
  migrationAssistantVersionToNewApiReducerBuilder,
  postMessageToAssistantReducerBuilder,
  postMessageWithResponsesReducerBuilder,
  uploadFileToAssistantDialogReducerBuilder,
} from "./reducers";
import type { AssistantsState } from "./types";
import { ChatCompletionsMessageObject } from "../../../services/api/instances/completionsApi/types";
import { THistory } from "../../../services/api/instances/responseApi/types/response";

const initialState: AssistantsState = {
  assistantValue: null,
  messages: [],
  assistantFunctions: [],
  skills: [],
  openAiAssistantsIds: [],
  isFunctionsLoading: false,
  messageLoadingInfo: {},
  isServiceLoading: false,
  isFileLoading: false,
  chatLogs: {},
  chatHistory: {},
};

const assistantsSlice = createSlice({
  name: "assistants",
  initialState,
  reducers: {
    setAssistantValue: (state, action: PayloadAction<AssistantValue>) => {
      state.assistantValue = {
        ...action.payload,
        assistant_object: {
          ...action.payload.assistant_object,
          response_format: "auto",
        },
      };
    },
    updateAssistantValue: {
      reducer(
        state: AssistantsState,
        action: PayloadAction<Partial<AssistantInfo>>,
      ) {
        if (!state.assistantValue?.assistant_object) return;
        Object.assign(state.assistantValue.assistant_object, action.payload);
      },
      prepare(updates: Partial<AssistantInfo>) {
        return {
          payload: updates,
        };
      },
    },
    setFunctionsAction: (state, action: PayloadAction<AssistantFunction[]>) => {
      state.assistantFunctions = action.payload;
    },
    setChatHistory: (
      state,
      action: PayloadAction<{ id: string; history: THistory[] }>,
    ) => {
      if (!action.payload) return;
      const { id, history } = action.payload;
      if (!state.chatHistory) {
        state.chatHistory = { id: history };
        return;
      }
      state.chatHistory[id] = history;
    },
    setOpenAiAssistantIds: (state, action: PayloadAction<string[]>) => {
      state.openAiAssistantsIds = action.payload;
    },
    addUserMessage: {
      reducer(
        state: AssistantsState,
        action: PayloadAction<ChatCompletionsMessageObject>,
      ) {
        state.messages.push(action.payload);
      },
      prepare(content: string) {
        return {
          payload: {
            role: "user",
            content,
          },
        };
      },
    },
    setChatLogs: (
      state,
      action: PayloadAction<{ id: string; log: string }>,
    ) => {
      const { id, log } = action.payload;
      state.chatLogs[id] = log;
    },
    setMessageLoadingInfo: (state, action: PayloadAction<string>) => {
      state.messageLoadingInfo[action.payload] = false;
    },
    clearMessagesAction: (state) => {
      state.messages = [];
    },

    clearAssistantsStateAction: () => initialState,
  },
  extraReducers: (builder) => {
    getAssistantFunctionsReducerBuilder(builder);
    deleteAssistantFunctionsReducerBuilder(builder);
    migrationAssistantVersionToNewApiReducerBuilder(builder);
    postMessageToAssistantReducerBuilder(builder);
    postMessageWithResponsesReducerBuilder(builder);
    getAssistantMessagesReducerBuilder(builder);
    uploadFileToAssistantDialogReducerBuilder(builder);
    getAssistantSkillsReducerBuilder(builder);
    changeResponseFormatReducerBuilder(builder);
  },
});

export const assistantsReducer = assistantsSlice.reducer;
export const {
  setChatLogs,
  setAssistantValue,
  updateAssistantValue,
  setOpenAiAssistantIds,
  setFunctionsAction,
  setChatHistory,
  clearMessagesAction,
  addUserMessage,
  setMessageLoadingInfo,
  clearAssistantsStateAction,
} = assistantsSlice.actions;
